<template>
    <div></div>
</template>

<script>
import { mapGetters } from 'vuex'
import Quill from 'quill'
import Highlighter from '../../helpers/Highlighter'
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html'
import AutoLinks from 'quill-auto-links'
import Emojis from '../../quill-modules/Emojis'
import Mention from 'quill-mention'
import '../../quill-modules/MentionBlot'

const Delta = Quill.import('delta')
const Keyboard = Quill.import('modules/keyboard')

Quill.register('modules/autoLinks', AutoLinks)
Quill.register('modules/emojis', Emojis)
Quill.register('modules/mention', Mention)

export default {
    props: {
        placeholder: {
            type: String,
            default: ''
        },

        toolbar: {
            type: Array,
            default () {
                return [
                    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                    ['bold', 'italic', 'underline', 'strike', 'link'],
                    [{ 'list': 'ordered' }, { 'list': 'bullet' }]
                ]
            }
        },

        value: {
            type: String
        },

        theme: {
            type: String,
            default: 'snow'
        },

        linkPlaceholder: {
            type: String,
            default: 'https://skybase.it'
        },

        emojis: {
            type: Boolean,
            required: false,
            default: false
        },

        emojiContainer: {
            type: String,
            required: false
        },

        tabindex: {
            type: String,
            required: false
        }
    },

    data () {
        return {
            editor: null,
            highlighter: new Highlighter()
        }
    },

    created () {
        require('quill-mention')
    },

    mounted () {
        let self = this

        let users = this.activeMembers.map((user) => {
            return { id: user.id, value: user.display_name }
        })

        this.editor = new Quill(this.$el, {
            theme: this.theme,
            placeholder: this.placeholder,
            bounds: this.bounds,
            modules: {
                keyboard: {
                    bindings: {
                        enter: {
                            key: Keyboard.keys.ENTER,
                            metaKey: true,
                            handler () {
                                self.$emit('cmd-enter')
                            }
                        }
                    }
                },
                toolbar: {
                    container: this.toolbar
                },
                clipboard: true,
                autoLinks: true,
                emojis: this.emojisModule,
                mention: {
                    allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
                    mentionDenotationChars: ['@'],
                    listItemClass: 'metionable__item',
                    source (searchTerm, renderList, mentionChar) {
                        let values

                        if (mentionChar === '@') {
                            values = users
                        }

                        if (searchTerm.length === 0) {
                            renderList(values, searchTerm)
                        } else {
                            const matches = []

                            for (let i = 0; i < values.length; i++) {
                                if (~values[i].value.toLowerCase().indexOf(searchTerm.toLowerCase())) {
                                    matches.push(values[i])
                                }
                            }

                            renderList(matches, searchTerm)
                        }
                    }
                }
            }
        })

        // Link placeholder
        let tooltip = this.editor.theme.tooltip
        let input = tooltip.root.querySelector('input[data-link]')
        input.dataset.link = this.linkPlaceholder

        // Initialise content
        if (this.value) {
            this.editor.clipboard.dangerouslyPasteHTML(this.value)
        }

        // Make sure this is initialise content, otherwise the content is not formatted
        // Paste always plain text
        this.editor.clipboard.addMatcher(Node.ELEMENT_NODE, function (node) {
            // we don't use $(node).text() because the format of the output is different from the original
            var plaintext = node.innerText

            return new Delta().insert(plaintext)
        })

        this.editor.on('text-change', () => {
            let text = this.getHtml()

            this.$emit('text-change', text)
            this.$emit('input', text)
        })

        if (this.tabindex) {
            this.editor.container.querySelector('.ql-editor').setAttribute('tabindex', this.tabindex)
        }
    },

    beforeDestroy () {
        let editor = document.querySelector('.ql-toolbar')

        if (editor) {
            editor.remove()
        }
    },

    methods: {
        focus () {
            this.editor.focus()
        },

        addEmoji (event) {
            let range = this.editor.getSelection(true)

            if (range.length > 0) {
                this.editor.deleteText(range.index, range.length)
            }

            this.editor.insertText(range.index, event.colons)

            this.$emit('emoji-picked')
        },

        getHtml () {
            let converter = new QuillDeltaToHtmlConverter(this.editor.getContents().ops, {})

            converter.renderCustomWith((blot) => {
                if (blot.insert.type === 'mention') {
                    return `<@${blot.insert.value.id}>`
                }

                if (blot.insert.type === 'emoji') {
                    return blot.insert.value
                }
            })

            return this.highlighter.highlight(converter.convert())
        },

        clear () {
            this.editor.setContents([{ insert: '\n' }])
        }
    },

    computed: {
        ...mapGetters('channel', ['activeMembers']),

        bounds () {
            if (this.theme === 'snow') {
                return document.getElementById('description')
            }

            return document.body
        },

        emojisModule () {
            if (this.emojis) {
                return {
                    // Parent element of emoji picker
                    container: this.emojiContainer || false
                }
            }

            return false
        }
    }
}
</script>
